import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Shield,
  Users,
  Crown,
  Rocket,
  Settings2,
  UserPlus,
  Mail,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Bug,
  Lightbulb,
  MessageSquare,
  Star,
  RefreshCw,
  Loader2,
  Copy,
  Eye,
  Search,
  Filter,
  BarChart3,
  TrendingUp,
  Pause,
  Play
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BetaManagement = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  
  // Settings state
  const [settings, setSettings] = useState({
    stylist_limit: 50,
    stylist_accepting: true,
    current_stylists: 0,
    stylist_slots_available: 50,
    soldier_limit: 100,
    soldier_accepting: true,
    current_soldiers: 0,
    soldier_slots_available: 100,
    super_soldier_limit: 4,
    super_soldier_accepting: true,
    current_super_soldiers: 0,
    super_soldier_slots_available: 4,
    beta_mode_enabled: true,
    beta_message: "",
    waitlist_enabled: true
  });
  
  // Waitlist state
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistStats, setWaitlistStats] = useState({ total: 0, pending: 0 });
  const [waitlistSearch, setWaitlistSearch] = useState("");
  
  // Feedback state
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ total: 0, new_bugs: 0, new_features: 0 });
  const [feedbackFilter, setFeedbackFilter] = useState("all");
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchWaitlist();
    fetchFeedback();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/beta/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch beta settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const response = await axios.get(`${API}/beta/waitlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitlist(response.data.entries || []);
      setWaitlistStats({ total: response.data.total, pending: response.data.pending });
    } catch (error) {
      console.error("Failed to fetch waitlist:", error);
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/beta/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.entries || []);
      setFeedbackStats({
        total: response.data.total,
        new_bugs: response.data.new_bugs,
        new_features: response.data.new_features
      });
    } catch (error) {
      console.error("Failed to fetch feedback:", error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/beta/settings`, {
        stylist_limit: settings.stylist_limit,
        stylist_accepting: settings.stylist_accepting,
        soldier_limit: settings.soldier_limit,
        soldier_accepting: settings.soldier_accepting,
        super_soldier_limit: settings.super_soldier_limit,
        super_soldier_accepting: settings.super_soldier_accepting,
        beta_mode_enabled: settings.beta_mode_enabled,
        beta_message: settings.beta_message,
        waitlist_enabled: settings.waitlist_enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Beta settings saved!");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const inviteFromWaitlist = async (email) => {
    try {
      const response = await axios.post(`${API}/beta/waitlist/${email}/invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite sent to ${email}`);
      fetchWaitlist();
    } catch (error) {
      toast.error("Failed to send invite");
    }
  };

  const removeFromWaitlist = async (email) => {
    try {
      await axios.delete(`${API}/beta/waitlist/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Removed from waitlist");
      fetchWaitlist();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      await axios.put(`${API}/beta/feedback/${feedbackId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Feedback updated");
      fetchFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredWaitlist = waitlist.filter(entry => {
    if (!waitlistSearch) return true;
    const query = waitlistSearch.toLowerCase();
    return entry.name?.toLowerCase().includes(query) || 
           entry.email?.toLowerCase().includes(query);
  });

  const filteredFeedback = feedback.filter(entry => {
    if (feedbackFilter === "all") return true;
    return entry.type === feedbackFilter;
  });

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
          <p className="text-stone-400 mt-2">Loading beta settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="beta-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              Beta Management
              <Badge className={settings.beta_mode_enabled ? "bg-green-500" : "bg-stone-500"}>
                {settings.beta_mode_enabled ? "BETA MODE ON" : "BETA MODE OFF"}
              </Badge>
            </h2>
            <p className="text-stone-400 text-sm">Control beta limits, waitlist, and feedback</p>
          </div>
        </div>
        <Button
          onClick={() => { fetchSettings(); fetchWaitlist(); fetchFeedback(); }}
          variant="outline"
          className="border-stone-600"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-xs">Stylists/Owners</p>
                <p className="text-2xl font-bold text-white">
                  {settings.current_stylists}/{settings.stylist_limit}
                </p>
              </div>
              <Users className={`w-8 h-8 ${settings.stylist_accepting ? "text-green-500" : "text-red-500"}`} />
            </div>
            <Progress 
              value={(settings.current_stylists / settings.stylist_limit) * 100} 
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-xs">Soldiers</p>
                <p className="text-2xl font-bold text-white">
                  {settings.current_soldiers}/{settings.soldier_limit}
                </p>
              </div>
              <Shield className={`w-8 h-8 ${settings.soldier_accepting ? "text-green-500" : "text-red-500"}`} />
            </div>
            <Progress 
              value={(settings.current_soldiers / settings.soldier_limit) * 100} 
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-xs">Super Soldiers</p>
                <p className="text-2xl font-bold text-white">
                  {settings.current_super_soldiers}/{settings.super_soldier_limit}
                </p>
              </div>
              <Crown className={`w-8 h-8 ${settings.super_soldier_accepting ? "text-green-500" : "text-red-500"}`} />
            </div>
            <Progress 
              value={(settings.current_super_soldiers / settings.super_soldier_limit) * 100} 
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-xs">Waitlist</p>
                <p className="text-2xl font-bold text-amber-400">{waitlistStats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-stone-500 text-xs mt-2">Total: {waitlistStats.total}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-800/50 border border-stone-700">
          <TabsTrigger value="settings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Settings2 className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="waitlist" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <UserPlus className="w-4 h-4 mr-2" />
            Waitlist ({waitlistStats.pending})
          </TabsTrigger>
          <TabsTrigger value="feedback" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <MessageSquare className="w-4 h-4 mr-2" />
            Feedback ({feedbackStats.new_bugs + feedbackStats.new_features})
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Global Beta Toggle */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-500" />
                Global Beta Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Beta Mode</Label>
                  <p className="text-stone-400 text-sm">When enabled, registration limits are enforced</p>
                </div>
                <Switch
                  checked={settings.beta_mode_enabled}
                  onCheckedChange={(checked) => setSettings({...settings, beta_mode_enabled: checked})}
                  className="data-[state=checked]:bg-purple-500"
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Waitlist Enabled</Label>
                  <p className="text-stone-400 text-sm">Allow users to join waitlist when full</p>
                </div>
                <Switch
                  checked={settings.waitlist_enabled}
                  onCheckedChange={(checked) => setSettings({...settings, waitlist_enabled: checked})}
                  className="data-[state=checked]:bg-amber-500"
                />
              </div>

              <div>
                <Label className="text-stone-300">Beta Message</Label>
                <Textarea
                  value={settings.beta_message}
                  onChange={(e) => setSettings({...settings, beta_message: e.target.value})}
                  placeholder="Message shown when registration is full..."
                  className="bg-stone-700 border-stone-600 text-white mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stylist/Owner Limits */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Stylist & Salon Owner Limits
              </CardTitle>
              <CardDescription className="text-stone-400">
                Current: {settings.current_stylists} registered
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  {settings.stylist_accepting ? (
                    <Play className="w-5 h-5 text-green-500" />
                  ) : (
                    <Pause className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <Label className="text-white font-medium">Accepting New Registrations</Label>
                    <p className="text-stone-400 text-sm">Toggle to pause/resume signups</p>
                  </div>
                </div>
                <Switch
                  checked={settings.stylist_accepting}
                  onCheckedChange={(checked) => setSettings({...settings, stylist_accepting: checked})}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Maximum Limit</Label>
                  <Input
                    type="number"
                    value={settings.stylist_limit}
                    onChange={(e) => setSettings({...settings, stylist_limit: parseInt(e.target.value) || 0})}
                    className="bg-stone-700 border-stone-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">Slots Available</Label>
                  <div className="mt-1 p-2.5 bg-stone-700 border border-stone-600 rounded-md text-white">
                    {settings.stylist_slots_available}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Soldier Limits */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Soldier Limits
              </CardTitle>
              <CardDescription className="text-stone-400">
                Current: {settings.current_soldiers} active soldiers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  {settings.soldier_accepting ? (
                    <Play className="w-5 h-5 text-green-500" />
                  ) : (
                    <Pause className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <Label className="text-white font-medium">Accepting New Soldiers</Label>
                    <p className="text-stone-400 text-sm">Toggle to pause/resume soldier applications</p>
                  </div>
                </div>
                <Switch
                  checked={settings.soldier_accepting}
                  onCheckedChange={(checked) => setSettings({...settings, soldier_accepting: checked})}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Maximum Limit</Label>
                  <Input
                    type="number"
                    value={settings.soldier_limit}
                    onChange={(e) => setSettings({...settings, soldier_limit: parseInt(e.target.value) || 0})}
                    className="bg-stone-700 border-stone-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">Slots Available</Label>
                  <div className="mt-1 p-2.5 bg-stone-700 border border-stone-600 rounded-md text-white">
                    {settings.soldier_slots_available}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Super Soldier Limits */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-500" />
                Super Soldier Limits
              </CardTitle>
              <CardDescription className="text-stone-400">
                Current: {settings.current_super_soldiers} super soldiers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  {settings.super_soldier_accepting ? (
                    <Play className="w-5 h-5 text-green-500" />
                  ) : (
                    <Pause className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <Label className="text-white font-medium">Accepting New Super Soldiers</Label>
                    <p className="text-stone-400 text-sm">Toggle to pause/resume promotions</p>
                  </div>
                </div>
                <Switch
                  checked={settings.super_soldier_accepting}
                  onCheckedChange={(checked) => setSettings({...settings, super_soldier_accepting: checked})}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Maximum Limit</Label>
                  <Input
                    type="number"
                    value={settings.super_soldier_limit}
                    onChange={(e) => setSettings({...settings, super_soldier_limit: parseInt(e.target.value) || 0})}
                    className="bg-stone-700 border-stone-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">Slots Available</Label>
                  <div className="mt-1 p-2.5 bg-stone-700 border border-stone-600 rounded-md text-white">
                    {settings.super_soldier_slots_available}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" /> Save Settings</>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Waitlist Tab */}
        <TabsContent value="waitlist" className="space-y-4">
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Beta Waitlist</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    value={waitlistSearch}
                    onChange={(e) => setWaitlistSearch(e.target.value)}
                    placeholder="Search..."
                    className="pl-9 bg-stone-700 border-stone-600 text-white w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredWaitlist.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus className="w-12 h-12 mx-auto text-stone-600 mb-3" />
                  <p className="text-stone-400">No one on the waitlist yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredWaitlist.map((entry, idx) => (
                    <div
                      key={entry.email}
                      className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <span className="text-amber-400 font-bold">#{idx + 1}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{entry.name}</p>
                          <p className="text-stone-400 text-sm">{entry.email}</p>
                        </div>
                        <Badge className={
                          entry.role === "owner" ? "bg-purple-500" :
                          entry.role === "soldier" ? "bg-amber-500" :
                          "bg-blue-500"
                        }>
                          {entry.role}
                        </Badge>
                        <Badge className={
                          entry.status === "invited" ? "bg-green-500" :
                          entry.status === "pending" ? "bg-yellow-500" :
                          "bg-stone-500"
                        }>
                          {entry.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-stone-500 text-sm">{formatDate(entry.created_at)}</span>
                        {entry.status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => inviteFromWaitlist(entry.email)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Invite
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromWaitlist(entry.email)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="space-y-4">
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Beta Feedback</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-500/20 text-red-400">
                    <Bug className="w-3 h-3 mr-1" />
                    {feedbackStats.new_bugs} Bugs
                  </Badge>
                  <Badge className="bg-blue-500/20 text-blue-400">
                    <Lightbulb className="w-3 h-3 mr-1" />
                    {feedbackStats.new_features} Features
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                {["all", "bug", "feature", "general", "praise"].map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant={feedbackFilter === type ? "default" : "outline"}
                    onClick={() => setFeedbackFilter(type)}
                    className={feedbackFilter === type ? "bg-amber-500 text-black" : "border-stone-600"}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {filteredFeedback.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 mx-auto text-stone-600 mb-3" />
                  <p className="text-stone-400">No feedback yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFeedback.map((entry) => (
                    <div
                      key={entry.feedback_id}
                      className="p-4 bg-stone-700/30 rounded-lg cursor-pointer hover:bg-stone-700/50"
                      onClick={() => setSelectedFeedback(entry)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            entry.type === "bug" ? "bg-red-500/20" :
                            entry.type === "feature" ? "bg-blue-500/20" :
                            entry.type === "praise" ? "bg-green-500/20" :
                            "bg-stone-600"
                          }`}>
                            {entry.type === "bug" ? <Bug className="w-5 h-5 text-red-400" /> :
                             entry.type === "feature" ? <Lightbulb className="w-5 h-5 text-blue-400" /> :
                             entry.type === "praise" ? <Star className="w-5 h-5 text-green-400" /> :
                             <MessageSquare className="w-5 h-5 text-stone-400" />}
                          </div>
                          <div>
                            <p className="text-white line-clamp-2">{entry.message}</p>
                            <p className="text-stone-500 text-sm mt-1">
                              {entry.user_email || "Anonymous"} • {formatDate(entry.created_at)}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          entry.status === "new" ? "bg-amber-500" :
                          entry.status === "resolved" ? "bg-green-500" :
                          "bg-stone-500"
                        }>
                          {entry.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Feedback Detail Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedFeedback?.type === "bug" && <Bug className="w-5 h-5 text-red-400" />}
              {selectedFeedback?.type === "feature" && <Lightbulb className="w-5 h-5 text-blue-400" />}
              {selectedFeedback?.type === "praise" && <Star className="w-5 h-5 text-green-400" />}
              {selectedFeedback?.type === "general" && <MessageSquare className="w-5 h-5 text-stone-400" />}
              {selectedFeedback?.type?.charAt(0).toUpperCase() + selectedFeedback?.type?.slice(1)} Feedback
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4 mt-4">
              <div className="bg-stone-700/30 p-4 rounded-lg">
                <p className="text-white">{selectedFeedback.message}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">From</p>
                  <p className="text-white">{selectedFeedback.user_email || "Anonymous"}</p>
                </div>
                <div>
                  <p className="text-stone-500">Date</p>
                  <p className="text-white">{formatDate(selectedFeedback.created_at)}</p>
                </div>
                {selectedFeedback.page_url && (
                  <div className="col-span-2">
                    <p className="text-stone-500">Page</p>
                    <p className="text-amber-400 text-xs">{selectedFeedback.page_url}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, "resolved")}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Resolved
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-stone-600"
                  onClick={() => updateFeedbackStatus(selectedFeedback.feedback_id, "in_progress")}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  In Progress
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BetaManagement;
